import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { Aperture, Cursor, Sparkle } from "@phosphor-icons/react";
import type { AlbumDocument, AlbumPage } from "../domain/project";
import { SafeImage } from "./SafeImage";

interface DepthPhotoGalleryPrototypeProps {
  document: AlbumDocument;
  urls: Record<string, string>;
  embedded?: boolean;
  headingId?: string;
}

const MAX_ROTATION = 9;
const MAX_TRANSLATION = 18;
const LERP = 0.08;

type PointerPosition = { x: number; y: number };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function DepthPhotoCard({ page, index, url }: { page: AlbumPage; index: number; url?: string }) {
  const cardRef = useRef<HTMLElement>(null);
  const animationRef = useRef<number | null>(null);
  const current = useRef<PointerPosition>({ x: 0, y: 0 });
  const target = useRef<PointerPosition>({ x: 0, y: 0 });
  const activeRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const [isActive, setIsActive] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      reducedMotionRef.current = media.matches;
      setReducedMotion(media.matches);
    };
    update();
    media.addEventListener?.("change", update);
    return () => {
      media.removeEventListener?.("change", update);
      if (animationRef.current !== null) window.cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const renderFrame = () => {
    animationRef.current = null;
    const card = cardRef.current;
    if (!card || reducedMotionRef.current) return;
    const nextX = current.current.x + (target.current.x - current.current.x) * LERP;
    const nextY = current.current.y + (target.current.y - current.current.y) * LERP;
    current.current = { x: nextX, y: nextY };
    card.style.setProperty("--depth-x", `${(nextX * MAX_TRANSLATION).toFixed(2)}px`);
    card.style.setProperty("--depth-y", `${(nextY * MAX_TRANSLATION).toFixed(2)}px`);
    card.style.setProperty("--depth-rotate-x", `${(-nextY * MAX_ROTATION).toFixed(2)}deg`);
    card.style.setProperty("--depth-rotate-y", `${(nextX * MAX_ROTATION).toFixed(2)}deg`);
    card.style.setProperty("--depth-spot-x", `${((nextX + 1) * 50).toFixed(1)}%`);
    card.style.setProperty("--depth-spot-y", `${((nextY + 1) * 50).toFixed(1)}%`);
    const settling = Math.abs(target.current.x - nextX) > 0.002 || Math.abs(target.current.y - nextY) > 0.002;
    if (activeRef.current || settling) animationRef.current = window.requestAnimationFrame(renderFrame);
  };

  const scheduleFrame = () => {
    if (reducedMotionRef.current || animationRef.current !== null) return;
    animationRef.current = window.requestAnimationFrame(renderFrame);
  };

  const setPointerTarget = (event: ReactPointerEvent<HTMLElement>) => {
    if (reducedMotionRef.current || event.pointerType === "touch" || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    target.current = {
      x: clamp((event.clientX - rect.left) / rect.width * 2 - 1, -1, 1),
      y: clamp((event.clientY - rect.top) / rect.height * 2 - 1, -1, 1),
    };
    scheduleFrame();
  };

  const resetPointerTarget = () => {
    target.current = { x: 0, y: 0 };
    activeRef.current = false;
    setIsActive(false);
    scheduleFrame();
  };

  const activate = (event?: ReactPointerEvent<HTMLElement>) => {
    if (event?.pointerType === "touch") return;
    activeRef.current = true;
    setIsActive(true);
    scheduleFrame();
  };

  const cardStyle = {
    "--depth-index": index,
  } as CSSProperties;

  return (
    <article
      ref={cardRef}
      className={`depth-photo-card${isActive ? " is-active" : ""}`}
      style={cardStyle}
      tabIndex={0}
      aria-label={`${page.title || "未命名照片"}，2.5D 活照片`}
      onPointerEnter={activate}
      onPointerMove={setPointerTarget}
      onPointerLeave={resetPointerTarget}
      onFocus={() => activate()}
      onBlur={resetPointerTarget}
    >
      <div className="depth-photo-surface">
        <span className="depth-photo-backdrop" aria-hidden="true" />
        <SafeImage src={url} alt={page.title || "影集照片"} className="depth-photo-image" loading={index < 3 ? "eager" : "lazy"} decoding="async" />
        <span className="depth-photo-haze" aria-hidden="true" />
        <span className="depth-photo-sheen" aria-hidden="true" />
        <span className="depth-photo-border" aria-hidden="true" />
        <div className="depth-photo-copy">
          <small>{String(index + 1).padStart(2, "0")} / LIVING PHOTO</small>
          <strong>{page.title || "未命名照片"}</strong>
          <span>{page.caption || "记忆不是静止的。"}</span>
        </div>
      </div>
    </article>
  );
}

export function DepthPhotoGalleryPrototype({ document, urls, embedded = false, headingId = "depth-photo-title" }: DepthPhotoGalleryPrototypeProps) {
  const pages = document.pages.slice(0, 6);
  return (
    <section className={`depth-photo-prototype${embedded ? " is-embedded" : ""}`} aria-labelledby={headingId}>
      <div className="depth-photo-heading">
        <div>
          <span className="eyebrow"><Sparkle size={14} /> 实验原型 / LIVING PHOTO</span>
          <h2 id={headingId}>让照片拥有一点深度。</h2>
          <p>把鼠标移到照片上，透视、光泽和轻微的视差会一起回应。它不是裁切，也不是滤镜，而是一张仍然保留完整内容的 2.5D 活照片。</p>
        </div>
        <span className="depth-photo-badge"><Aperture size={16} /> 2.5D / POINTER DEPTH</span>
      </div>
      {pages.length ? (
        <div className="depth-photo-grid" aria-label="2.5D 活照片列表">
          {pages.map((page, index) => <DepthPhotoCard key={page.id} page={page} index={index} url={urls[page.assetId]} />)}
        </div>
      ) : (
        <div className="depth-photo-empty"><Cursor size={30} /><strong>还没有照片</strong><span>导入照片后，这里会变成可以回应鼠标的活照片。</span></div>
      )}
      <div className="depth-photo-note"><Cursor size={15} /> 移入照片查看深度；触屏和减少动效设置会自动保持静态阅读。</div>
    </section>
  );
}
